import { useStore } from 'lib/store'
import { ComponentProps, styled, theme } from 'lib/theme'
import { observer } from 'mobx-react-lite'
import { Box } from 'primitives/Box'
import { Flex } from 'primitives/Flex'
import { Children, ReactElement, cloneElement, useCallback, useEffect, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import { DesktopControl } from './components/DesktopControl'
import { DotsTrack } from './components/DotsTrack'

const SCREEN_MOVEMENT_TRANSITION_DURATION = 900

const SlideWrapper = styled(Box, {
  opacity: '1',
  width: '100%',
  height: '100%',
  minWidth: '100%',
  maxWidth: '100%',
  overflowX: 'hidden',
  maxHeight: '100vh',
  overflowY: 'hidden'
})

const CarouselContainer = styled(Flex, {
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  backgroundSize: 'contain'
})

const CarouselTrack = styled(Flex, {
  position: 'relative',
  height: '100%',
  width: '100%',
  maxHeight: '100vh'
})

export interface CarouselProps extends ComponentProps<typeof Flex> {
  touchEnabled?: boolean
  dots?: boolean
  controls?: boolean
  initialIndex?: number
  onSlide?: (index: number) => void
  // Should be absolute positioned relative to CarouselContainer
  customControls?: (activeIndex: number) => React.ReactNode
}

const _Carousel: React.FC<CarouselProps> = ({
  children,
  touchEnabled = false,
  dots = true,
  controls = true,
  initialIndex = 0,
  onSlide,
  customControls,
  css
}) => {
  const numSlides = Children.count(children)
  const [activeIndex, setActiveIndex] = useState(initialIndex)
  const hasPrev = activeIndex > 0
  const hasNext = activeIndex < numSlides - 1
  const { ui } = useStore()

  const handleLeft = useCallback(() => {
    const index = hasPrev ? activeIndex - 1 : activeIndex
    setActiveIndex(index)
    onSlide?.(index)
  }, [activeIndex, hasPrev, onSlide])

  const handleRight = useCallback(() => {
    const index = hasNext ? activeIndex + 1 : 0
    setActiveIndex(index)
    onSlide?.(index)
  }, [activeIndex, hasNext, onSlide])

  const bindTouchHandlers = useSwipeable({
    onSwipedLeft: () => handleRight(),
    onSwipedRight: () => handleLeft(),
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: false
  })

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (
        (event.target as HTMLElement)?.tagName === 'TEXTAREA' ||
        !ui.showPostModal ||
        touchEnabled
      ) {
        return
      }

      if ((event.key === 'a' || event.key === 'ArrowLeft') && hasPrev) {
        handleLeft()
      } else if ((event.key === 'd' || event.key === 'ArrowRight') && hasNext) {
        handleRight()
      }
    }

    document.addEventListener('keydown', handleKeyPress)

    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [handleLeft, handleRight, hasNext, hasPrev, touchEnabled, ui.showPostModal])

  return (
    <CarouselContainer css={css}>
      <CarouselTrack
        className="carousel-track"
        css={{
          left: `-${activeIndex * 100}%`,
          transition: `left ${SCREEN_MOVEMENT_TRANSITION_DURATION}ms ${theme.transitions.easeOutExpo}`
        }}
        {...bindTouchHandlers}
      >
        {Children.map(children, (child, i) => (
          <SlideWrapper>
            {cloneElement(child as ReactElement, {
              isActive: activeIndex === i
            })}
          </SlideWrapper>
        ))}
      </CarouselTrack>

      {/* Should be absolute positioned (relative to CarouselContainer) */}
      {controls && !ui.isMobile && (
        <>
          <DesktopControl
            padding={ui.showPostModal}
            isDisabled={numSlides === 1}
            pos="left"
            show={true}
            onClick={handleLeft}
          />
          <DesktopControl
            padding={ui.showPostModal}
            isDisabled={numSlides === 1}
            pos="right"
            show={true}
            onClick={handleRight}
          />
        </>
      )}
      {customControls?.(activeIndex)}

      {dots && numSlides > 1 && <DotsTrack num={numSlides} index={activeIndex} />}
    </CarouselContainer>
  )
}
export const Carousel = observer(_Carousel)
